'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuthStore } from '@/stores/authStore';

interface AuthGuardProps {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const router = useRouter();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const [isChecking, setIsChecking] = useState(true);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const verify = async () => {
      try {
        const result = await checkAuth();
        if (isMounted) {
          // 고객 계정은 내부 화면 대신 고객 포털로 이동 (client-portal)
          const isClient = result && useAuthStore.getState().user?.role === 'client';
          setIsValid(result && !isClient);
          setIsChecking(false);
          if (!result) {
            router.push('/login');
          } else if (isClient) {
            router.replace('/client');
          }
        }
      } catch {
        if (isMounted) {
          setIsValid(false);
          setIsChecking(false);
          router.push('/login');
        }
      }
    };

    verify();

    return () => {
      isMounted = false;
    };
  }, [checkAuth, router]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f5f7]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">인증 확인 중...</p>
        </div>
      </div>
    );
  }

  if (!isValid || !isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
